import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  AuthRenderState,
  Locale,
  LoyaltyCurrency,
  NavLink,
  PointsSummary,
} from '@rd/types';
import { Observable } from 'rxjs';
import { ChangeLocale } from '../../locale/store/actions/locale.actions';
import { getLocaleSelected } from '../../locale/store/selectors/locale.selectors';
import { NavLinksService } from '../../services/dropdown-link-items/nav-links.service';
import { GuardhouseService } from '../../services/guardhouse/guardhouse.service';
import { UrlUtilsService } from '../../services/url-utils/url-utils.service';
import { CoreState } from '../../store/reducers';
import { getTenantBootstrapLocales } from '../../tenant/store/selectors/tenant.selectors';

type headerLogoClass = 'dashboard-page' | 'non-dashboard-page';

// Component's render state is based off of AuthRenderState type. Add other render states here, if they come.
type ComponentRenderState = AuthRenderState;

@Component({
  selector: 'rd-core-main-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent implements OnInit {
  @HostBinding('class.rd-core-main-header') componentClass = true;
  @Input() authRenderState: AuthRenderState;
  @Input() pointsSummary: PointsSummary;
  @Input() loyaltyCurrencySelected: LoyaltyCurrency;
  @Output() toggleSideNav: EventEmitter<void> = new EventEmitter<void>();
  @Output() logoutEvent: EventEmitter<void> = new EventEmitter<void>();

  tenantLocales$: Observable<Locale[] | null>;
  localeSelected$: Observable<Locale | null>;

  navLinks: NavLink[];
  logoutNavLink: NavLink;
  dropdownShown = false;
  showBackButton: boolean;

  constructor(
    protected cdRef: ChangeDetectorRef,
    protected guardhouseService: GuardhouseService,
    protected navLinksService: NavLinksService,
    protected location: Location,
    protected router: Router,
    protected urlUtilsService: UrlUtilsService,
    protected store: Store<CoreState>,
  ) {
    this.showBackButton = !this.router.url.startsWith('/dashboard');
    this.navLinks = this.navLinksService.allNavLinks;
    this.logoutNavLink = this.navLinksService.logoutNavLink;
  }

  ngOnInit(): void {
    this.setupSubscriptions();
  }

  getComponentRenderState(): ComponentRenderState {
    if (this.authRenderState === 'loggedIn' && !this.pointsSummary) {
      return 'loading';
    }

    return this.authRenderState;
  }

  login(): void {
    this.guardhouseService.redirectToLoginUrl();
  }

  private setupSubscriptions(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.showBackButton = !this.router.url.startsWith('/dashboard');
        this.cdRef.detectChanges();
      }
    });

    this.tenantLocales$ = this.store.select(getTenantBootstrapLocales);
    this.localeSelected$ = this.store.select(getLocaleSelected);
  }

  goBack(): void {
    this.location.back();
  }

  logoClass(): headerLogoClass {
    if (this.router.url.startsWith('/dashboard')) {
      return 'dashboard-page';
    } else {
      return 'non-dashboard-page';
    }
  }

  changeLocale(locale: Locale): void {
    this.store.dispatch(new ChangeLocale(locale));
  }

  toggleDropdown(): void {
    if (this.getComponentRenderState() === 'loggedIn') {
      this.dropdownShown = !this.dropdownShown;
    }
  }

  closeDropdown(): void {
    if (this.dropdownShown) {
      this.dropdownShown = false;
    }
  }

  toggleSideNavClick(): void {
    this.toggleSideNav.emit();
  }

  logout(): void {
    this.logoutEvent.emit();
  }

  currencyShortName(): string {
    return this.loyaltyCurrencySelected?.shortName;
  }

  /* ===================== */
  /* ==== Image URLs ===== */

  loginImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/car-icon-blue.png');
  }

  loginImageHoverUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/car-icon-white.png');
  }

  logoImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/header-logo.png');
  }

  backArrowImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd-back-arrow.png');
  }

  faqIconDesktopImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd-faq-icon-desktop.png');
  }

  notifIconWhiteImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd-notif-icon-white.png');
  }

  downWhiteArrowImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd-down-whitearrow.png');
  }

  logoutImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd-myprofile-icon.png');
  }

  /* ===================== */
}
